import { SeverityTools, SolSeverity, SolTag, SolText } from '@sol/components';
import { SolIcon } from '@sol/icons';

/**
 * Score normalization tools to ensure compatibility between legacy Vue and Angular SOL
 * grade calculations.
 *
 * Legacy thresholds (getLetterFromScore):
 *   F: 0.00 - 0.20
 *   E: 0.20 - 0.40
 *   D: 0.40 - 0.60
 *   C: 0.60 - 0.80
 *   B: 0.80 - 0.97
 *   A: 0.97 - 1.00
 *
 * Angular toGradeText uses equal 1/6 intervals with maxValue=1:
 *   F: 0.000 - 0.167
 *   E: 0.167 - 0.333
 *   D: 0.333 - 0.500
 *   C: 0.500 - 0.667
 *   B: 0.667 - 0.833
 *   A: 0.833 - 1.000
 *
 * This normalizer maps scores so that SolSeverity.toGradeText() produces the same
 * letter as the legacy getLetterFromScore() function.
 */
export class AsmScoreTools {
  /**
   * Legacy thresholds defining score ranges for each grade.
   * Each entry: [minScore, maxScore, targetAngularMidpoint]
   * The targetAngularMidpoint is the center of the corresponding Angular grade range.
   */
  private static readonly LEGACY_THRESHOLDS: Array<{
    min: number;
    max: number;
    angularMid: number;
  }> = [
    { min: 0.0, max: 0.2, angularMid: 0.083 }, // F -> Angular index 0 (0.000-0.167)
    { min: 0.2, max: 0.4, angularMid: 0.25 }, // E -> Angular index 1 (0.167-0.333)
    { min: 0.4, max: 0.6, angularMid: 0.417 }, // D -> Angular index 2 (0.333-0.500)
    { min: 0.6, max: 0.8, angularMid: 0.583 }, // C -> Angular index 3 (0.500-0.667)
    { min: 0.8, max: 0.97, angularMid: 0.75 }, // B -> Angular index 4 (0.667-0.833)
    { min: 0.97, max: 1.0, angularMid: 0.917 }, // A -> Angular index 5 (0.833-1.000)
  ];

  /**
   * Normalizes a score (0-1) so that when passed to SolSeverity.toGradeText(),
   * it produces the same letter grade as the legacy getLetterFromScore() function.
   *
   * @param score - Original score value (0-1 range)
   * @returns Normalized score that produces matching grade in toGradeText
   */
  public static normalizeScoreForGrade(score: number | undefined): number | undefined {
    if (score === undefined || score === null) {
      return undefined;
    }

    // Clamp score to valid range
    const clampedScore = Math.max(0, Math.min(1, score));

    // Handle edge cases
    if (clampedScore >= 1) {
      return 0.917; // Maps to A in Angular
    }
    if (clampedScore <= 0) {
      return 0.083; // Maps to F in Angular
    }

    // Find the legacy threshold this score falls into
    for (const threshold of AsmScoreTools.LEGACY_THRESHOLDS) {
      if (clampedScore >= threshold.min && clampedScore < threshold.max) {
        return threshold.angularMid;
      }
    }

    // Fallback for score === 1.0 (edge case)
    return 0.917;
  }

  /** Maps a score letter grade ("A"-"F") to a grade-formatted SolSeverity.
   * Used by the data source view derivation (backend returns letter strings). */
  public static letterToSeverity(letter: string | undefined | null): SolSeverity {
    const midpoints: Record<string, number> = {
      F: 0.083,
      E: 0.25,
      D: 0.417,
      C: 0.583,
      B: 0.75,
      A: 0.917,
    };
    const normalizedScore = letter ? midpoints[letter.toUpperCase()] : undefined;
    return SolSeverity.of(normalizedScore)
      .withMaxValue(1)
      .withFormat('grade')
      .withFavor('high')
      .withDecimalCount(0)
      .withMode('inline');
  }

  /** Maps a score float (0-1) to a grade-formatted SolSeverity. */
  public static scoreToSeverity(score: number | undefined | null): SolSeverity {
    const numericScore = typeof score === 'number' && !isNaN(score) ? score : undefined;
    const normalizedScore = AsmScoreTools.normalizeScoreForGrade(numericScore);
    return SolSeverity.of(normalizedScore)
      .withMaxValue(1)
      .withFormat('grade')
      .withFavor('high')
      .withDecimalCount(0)
      .withMode('inline');
  }

  /** The grade letter as a coloured tag — shared by the asset score badge and the tracker's own score changes. */
  public static scoreToTag(score: number | undefined | null): SolTag {
    const severity = AsmScoreTools.scoreToSeverity(score);
    return SolTag.of(SolText.ofRaw(severity.toText()))
      .withTextColor('ink')
      .withTextStyle('bold')
      .withIcon(SolIcon.circle_filled_small)
      .withIconColor(SeverityTools.calculateColor(severity));
  }
}
